<script setup lang="ts">
import { SolButton, useToast } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import DownloadIcon from '~icons/material-symbols/download'
import SharedCardSession from '~/components/shared/CardSession.vue'
import { getPartnerArrearsList } from '~/services-v2/solfacil-plus-api/solfacil-plus-api'
import useAuth from '~/store-v2/auth/useAuth'
import { useSolfacilPlusInfo } from '~/store-v2/solfacilplus/useSolfacilPlusInfo'
import { useImpersonateStore } from '~/store/impersonate'

const { t } = useI18n()
const toast = useToast()
const { data: auth } = useAuth()
const impersonateStore = useImpersonateStore()

const basePartnerId = computed(() => {
  const perfil = auth.value?.me?.perfil
  if (perfil === 'admin' && impersonateStore.partnerId > 0) {
    return impersonateStore.partnerId.toString()
  }
  return auth.value?.person?.partner?.id?.toString() || ''
})

const { data: solfacilPlus, isLoading } = useSolfacilPlusInfo(basePartnerId)

const walletHealthHasArrears = computed(
  () => solfacilPlus.value?.partnerGoals?.walletHealth?.hasArrears || false,
)

const isAdminNotImpersonating = computed(() => {
  return auth.value?.me?.perfil === 'admin' && !impersonateStore.verifyIsImpersonated()
})

const isEmpty = computed(() => !solfacilPlus.value)

function handleOpenImpersonateModal() {
  impersonateStore.toggleImpersonateModal()
}

async function handleDownloadArrearsList() {
  if (!basePartnerId.value)
    return

  try {
    const data = await getPartnerArrearsList(basePartnerId.value)
    if (data?.url) {
      window.open(data.url, '_blank')
    }
  }
  catch {
    toast.createErrorToast('Erro ao baixar lista de inadimplentes')
  }
}
</script>

<template>
  <SharedCardSession :title="t('overview.wallet_health.title')">
    <template #content>
      <div class="wallet-health">
        <div
          v-if="isAdminNotImpersonating"
          class="wallet-health-not-impersonate"
        >
          <div class="wallet-health-not-impersonate-icon">
            <img
              src="/images/period-payment.svg"
              alt="Period payment"
              class="icon"
            >
          </div>
          <div class="wallet-health-not-impersonate-content">
            <h4 class="wallet-health-not-impersonate-title">
              {{ t('overview.wallet_health.not_impersonate.title') }}
            </h4>
            <p class="wallet-health-not-impersonate-description">
              {{ t('overview.wallet_health.not_impersonate.description') }}
            </p>
            <SolButton
              id="wallet-health-view-partner"
              size="medium"
              class="wallet-health-not-impersonate-button"
              @click="handleOpenImpersonateModal"
            >
              {{ t('overview.wallet_health.not_impersonate.button') }}
            </SolButton>
          </div>
        </div>

        <template v-else-if="!isEmpty || isLoading">
          <div class="wallet-health-content">
            <div class="wallet-health-hero" :class="{ 'wallet-health-hero--has-arrears': !isLoading && walletHealthHasArrears }">
              <img
                src="/images/wallethealth.svg"
                :alt="t('overview.wallet_health.image_alt')"
                class="wallet-health-illustration"
              >

              <div class="wallet-health-intro">
                <p class="wallet-health-intro-text">
                  {{ t('overview.wallet_health.intro.description1_prefix') }}
                  <span class="wallet-health-intro-highlight">
                    {{ t('overview.wallet_health.intro.description1_highlight1') }}
                  </span>
                  {{ t('overview.wallet_health.intro.description1_middle') }}
                  <span class="wallet-health-intro-highlight">
                    {{ t('overview.wallet_health.intro.description1_highlight2') }}
                  </span>
                  {{ t('overview.wallet_health.intro.description1_suffix') }}
                </p>
                <p class="wallet-health-intro-text">
                  {{ t('overview.wallet_health.intro.description2') }}
                </p>
              </div>

              <div
                v-if="!isLoading && walletHealthHasArrears"
                class="wallet-health-arrears-card"
              >
                <div class="wallet-health-arrears-content">
                  <h4 class="wallet-health-arrears-title">
                    {{ t('overview.wallet_health.arrears.title') }}
                  </h4>
                  <p class="wallet-health-arrears-description">
                    {{ t('overview.wallet_health.arrears.description') }}
                  </p>
                </div>

                <SolButton
                  id="download-client-list"
                  size="small"
                  class="wallet-health-arrears-button"
                  @click="handleDownloadArrearsList"
                >
                  <template #icon-left>
                    <DownloadIcon class="w-5 h-5" />
                  </template>
                  {{ t('overview.wallet_health.arrears.button') }}
                </SolButton>
              </div>
            </div>

            <div class="wallet-health-divider" />

            <div
              v-if="!isLoading"
              class="wallet-health-info"
            >
              <h4 class="wallet-health-title">
                {{ t('overview.wallet_health.info.improve.title') }}
              </h4>
              <div class="wallet-health-tips">
                <ul class="wallet-health-tips-list">
                  <li>{{ t('overview.wallet_health.info.improve.tip1') }}</li>
                  <li>{{ t('overview.wallet_health.info.improve.tip2') }}</li>
                  <li>{{ t('overview.wallet_health.info.improve.tip3') }}</li>
                </ul>
              </div>
            </div>
          </div>
        </template>

        <div
          v-else
          class="wallet-health-empty"
        >
          <h4 class="wallet-health-title">
            {{ t('overview.wallet_health.how_works.title') }}
          </h4>
          <div class="wallet-health-description-text">
            <p>{{ t('overview.wallet_health.how_works.description1') }}</p>
            <p>{{ t('overview.wallet_health.how_works.description2') }}</p>
          </div>
        </div>
      </div>
    </template>
  </SharedCardSession>
</template>

<style scoped lang="scss">
.wallet-health {
  @apply flex flex-col gap-6 w-full;

  &-content {
    @apply flex flex-col gap-6 w-full;
  }

  &-hero {
    @apply flex flex-col gap-6 w-full;
  }

  &-illustration {
    @apply w-full h-auto;
  }

  &-intro {
    @apply flex flex-col gap-4;
  }

  &-intro-text {
    @apply fonts-body-medium-regular text-feedback-informative-dark;
    line-height: 1.15;
  }

  &-intro-highlight {
    @apply text-feedback-informative-dark fonts-body-medium-bold;
  }

  &-arrears-card {
    @apply flex flex-col gap-6 p-4 rounded-md bg-neutral-high-light;
    width: 100%;
    box-sizing: border-box;
  }

  &-arrears-content {
    @apply flex flex-col gap-4;
  }

  &-arrears-title {
    @apply fonts-heading-h4 text-neutral-low-dark;
    line-height: 1.15;
  }

  &-arrears-description {
    @apply fonts-body-medium-regular text-feedback-informative-dark;
    line-height: 1.15;
  }

  &-arrears-button {
    @apply self-start mt-auto;
  }

  &-divider {
    @apply bg-neutral-high-medium h-px w-full;
    opacity: 0.64;
  }

  &-info {
    @apply flex flex-col gap-4 items-start w-full;

    .wallet-health-title {
      @apply fonts-heading-h4 text-neutral-low-pure;
    }

    .wallet-health-tips {
      @apply w-full;

      &-list {
        @apply flex flex-col gap-2 ml-nano self-stretch;
        @apply fonts-body-medium-regular text-feedback-informative-dark;
        @apply list-disc;
      }
    }
  }

  &-empty {
    @apply flex flex-col gap-4 w-full;

    .wallet-health-title {
      @apply fonts-heading-h4 text-neutral-low-dark;
    }

    .wallet-health-description-text {
      @apply flex flex-col gap-4;
      @apply fonts-body-large-regular text-neutral-low-light;
    }
  }

  &-not-impersonate {
    @apply flex flex-col items-center justify-center gap-4 w-full;
    @apply py-10 px-12;

    &-icon {
      @apply flex items-center justify-center;
      @apply w-24 h-24;

      .icon {
        @apply w-full h-full;
      }
    }

    &-content {
      @apply flex flex-col items-center gap-2 w-full;

      .wallet-health-not-impersonate-title {
        @apply fonts-heading-h4 text-neutral-low-dark;
      }

      .wallet-health-not-impersonate-description {
        @apply fonts-body-medium-regular text-neutral-low-light;
      }

      .wallet-health-not-impersonate-button {
        @apply mt-4;
      }
    }
  }
}

$arrears-card-side-width: 200px;

@media (max-width: 1023px) {
  .wallet-health {
    &-hero--has-arrears {
      @apply flex flex-col gap-6;
    }

    &-hero--has-arrears .wallet-health-illustration {
      @apply w-full max-w-none;
    }

    &-hero--has-arrears .wallet-health-intro {
      @apply max-w-none;
    }

    &-hero--has-arrears .wallet-health-arrears-card {
      @apply w-full max-w-none;
    }
  }
}

@media (min-width: 1024px) and (max-width: 1365px) {
  .wallet-health {
    &-hero--has-arrears {
      grid-template-columns: minmax(0, 1fr) #{$arrears-card-side-width};
      grid-template-areas:
        'image image'
        'intro arrears';
      @apply grid items-stretch gap-6 justify-items-start;
    }

    &-hero--has-arrears .wallet-health-illustration {
      grid-area: image;
      @apply w-full max-w-none justify-self-stretch;
    }

    &-hero--has-arrears .wallet-health-intro {
      grid-area: intro;
      @apply max-w-none;
    }

    &-hero--has-arrears .wallet-health-arrears-card {
      grid-area: arrears;
      justify-self: stretch;
      width: $arrears-card-side-width;
      max-width: $arrears-card-side-width;
      @apply h-full min-h-0 self-stretch;
    }
  }
}

@media (min-width: 1366px) {
  .wallet-health {
    &-hero--has-arrears {
      grid-template-columns: minmax(0, 375px) #{$arrears-card-side-width};
      grid-template-areas:
        'image arrears'
        'intro arrears';
      column-gap: 24px;
      row-gap: 24px;
      @apply grid items-stretch justify-items-start;
    }

    &-hero--has-arrears .wallet-health-illustration {
      grid-area: image;
      @apply w-full max-w-[375px] justify-self-start;
    }

    &-hero--has-arrears .wallet-health-intro {
      grid-area: intro;
      @apply max-w-[375px];
    }

    &-hero--has-arrears .wallet-health-arrears-card {
      grid-area: arrears;
      justify-self: stretch;
      width: $arrears-card-side-width;
      max-width: $arrears-card-side-width;
      @apply h-full min-h-0;
    }
  }
}
</style>
